import { api } from '../api/client.js';
import { createNewsCard, type NewsCardData } from '../components/news-card.js';
import { createTabBar } from '../components/tab-bar.js';
import { createSkeleton } from '../components/skeleton.js';
import { getRouter } from '../router.js';

interface NewsListResponse {
  items: NewsCardData[];
  total: number;
  limit: number;
  offset: number;
}

interface TagsResponse {
  tags: string[];
}

export async function homePage(): Promise<HTMLElement> {
  const router = getRouter();
  const page = document.createElement('div');
  page.className = 'page page--home';
  page.setAttribute('data-title', 'Главная');

  // Header
  const header = document.createElement('header');
  header.className = 'page-header';
  header.innerHTML = `
  <svg class="page-header__logo" width="32" height="28" viewBox="0 0 32 28" fill="none" aria-hidden="true">
    <polyline points="2,14 16,2 30,14" stroke="#4a7c2f" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
    <rect x="6" y="14" width="20" height="12" rx="1" stroke="#4a7c2f" stroke-width="1.5" fill="none"/>
    <rect x="13" y="19" width="6" height="7" rx="1" stroke="#4a7c2f" stroke-width="1.5" fill="none"/>
  </svg>
  <div class="page-header__text">
    <h1 class="page-header__title">СНТ "ЭЛЕКТРОН"</h1>
    <span class="page-header__subtitle">Сайт садоводов СНТ "Электрон"</span>
  </div>`;
  page.appendChild(header);

  // Tabs
  const featureListings = import.meta.env.VITE_FEATURE_LISTINGS === 'true';
  const tabs = createTabBar([
    { id: 'news', label: 'Новости', href: '/' },
    { id: 'contacts', label: 'Контакты', href: '/contacts' },
    { id: 'board', label: 'Объявления', href: '/board', hidden: !featureListings },
    { id: 'bus', label: 'Автобус', href: '/bus' },
  ], 'news', (href) => router.navigate(href));
  page.appendChild(tabs);

  // Content
  const content = document.createElement('div');
  content.className = 'page-content';

  const skeleton = createSkeleton('card', 3);
  content.appendChild(skeleton);
  page.appendChild(content);

  // Read initial tag from URL
  const initialTag = new URLSearchParams(window.location.search).get('tag') || null;

  try {
    const [tagsData, data] = await Promise.all([
      api.get<TagsResponse>('/news/tags').catch(() => ({ tags: [] as string[] })),
      api.get<NewsListResponse>('/news', initialTag ? { limit: 20, offset: 0, tag: initialTag } : { limit: 20, offset: 0 }),
    ]);

    content.innerHTML = '';

    let activeTag: string | null = initialTag;

    // Tag filter chips
    if (tagsData.tags.length > 0) {
      const tagsContainer = document.createElement('div');
      tagsContainer.className = 'news-tags-filter';

      function renderTagChips(): void {
        tagsContainer.innerHTML = '';

        const allChip = document.createElement('button');
        allChip.className = `tag-chip${activeTag === null ? ' tag-chip--active' : ''}`;
        allChip.textContent = 'Все';
        allChip.addEventListener('click', () => {
          activeTag = null;
          updateUrl(null);
          renderTagChips();
          loadNews();
        });
        tagsContainer.appendChild(allChip);

        for (const tag of tagsData.tags) {
          const chip = document.createElement('button');
          chip.className = `tag-chip${activeTag === tag ? ' tag-chip--active' : ''}`;
          chip.textContent = tag;
          chip.addEventListener('click', () => {
            activeTag = tag;
            updateUrl(tag);
            renderTagChips();
            loadNews();
          });
          tagsContainer.appendChild(chip);
        }
      }

      renderTagChips();
      content.appendChild(tagsContainer);
    }

    const newsContainer = document.createElement('div');
    newsContainer.className = 'news-list';
    content.appendChild(newsContainer);

    function updateUrl(tag: string | null): void {
      const url = tag ? `/?tag=${encodeURIComponent(tag)}` : '/';
      window.history.replaceState(null, '', url);
    }

    async function loadNews(): Promise<void> {
      newsContainer.innerHTML = '';
      const sk = createSkeleton('card', 2);
      newsContainer.appendChild(sk);
      try {
        const params: Record<string, string | number> = { limit: 20, offset: 0 };
        if (activeTag) params.tag = activeTag;
        const d = await api.get<NewsListResponse>('/news', params);
        newsContainer.innerHTML = '';
        if (d.items.length === 0) {
          const empty = document.createElement('div');
          empty.className = 'empty-state';
          empty.innerHTML = '<p>Новостей пока нет</p>';
          newsContainer.appendChild(empty);
        } else {
          for (const news of d.items) {
            newsContainer.appendChild(createNewsCard(news, (href) => router.navigate(href)));
          }
        }
      } catch {
        newsContainer.innerHTML = '';
        const error = document.createElement('div');
        error.className = 'empty-state';
        error.innerHTML = '<p>Ошибка загрузки новостей</p>';
        newsContainer.appendChild(error);
      }
    }

    // Render initial data (already fetched)
    if (data.items.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'empty-state';
      empty.innerHTML = '<p>Новостей пока нет</p>';
      newsContainer.appendChild(empty);
    } else {
      for (const news of data.items) {
        newsContainer.appendChild(createNewsCard(news, (href) => router.navigate(href)));
      }
    }

  } catch (err) {
    content.innerHTML = '';
    const error = document.createElement('div');
    error.className = 'empty-state';
    error.innerHTML = '<p>Ошибка загрузки новостей</p>';
    content.appendChild(error);
    console.error(err);
  }

  return page;
}
